import { ID, PageEvents } from '@griply/types';
import {
  createDefaultCompletedFilterDocument,
  getCompletedFilterDocument,
} from '@griply/utils';
import { useUpdateUserSettings } from 'features/user';
import React, { useMemo, useRef, useState } from 'react';
import { useToday } from 'shared/contexts/today';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';

import * as Styled from './completed.style';
import { PageTopBar } from './components/page-top-bar/index';
import { TasksOverview } from './components/tasks-overview/index';

export const Completed: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.Completed, undefined);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const today = useToday();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();

  const [collapsedSections, setCollapsedSections] = useState<ID[]>([]);

  const showHabits = !user?.settings?.completed?.hideHabits;

  const defaultFilter = useMemo(
    () => createDefaultCompletedFilterDocument(today),
    [today],
  );

  const filterDocument = useMemo(() => {
    const completedFilter = getCompletedFilterDocument(today, showHabits);
    if (!collapsedSections.length) {
      return completedFilter;
    }

    return { ...completedFilter, collapsedSections };
  }, [today, showHabits, collapsedSections]);

  const toggleShowHabits = () =>
    updateUserSettings({ completed: { hideHabits: showHabits } });

  const resetFilters = () => {
    setCollapsedSections([]);
    updateUserSettings({ completed: { hideHabits: false } });
  };

  return (
    <Styled.Container>
      <Styled.Main>
        <Styled.MainContent>
          <PageTopBar
            showHabits={showHabits}
            onToggleShowHabits={toggleShowHabits}
          />
          <Styled.Content ref={scrollContainerRef}>
            <TasksOverview
              scrollElement={scrollContainerRef}
              filterDocument={filterDocument}
              defaultFilter={defaultFilter}
              onSectionCollapse={setCollapsedSections}
              onReset={resetFilters}
            />
          </Styled.Content>
        </Styled.MainContent>
      </Styled.Main>
    </Styled.Container>
  );
};
